import React, { useState } from 'react';
import { Button, Space, message } from 'antd';
import { UserOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import IndicatorCardWithCustomTitle from './components/IndicatorCardWithCustomTitle';
import TablePaginationBar from '../../../../components/TablePaginationBar';
import TaskMonitoringFilter from './components/TaskMonitoringFilter';
import TaskMonitoringTable from './components/TaskMonitoringTable';
import DailyTaskDetailDrawer from './components/DailyTaskDetailDrawer';
import CentralParcelDetailDrawer from '../../../central/parcels/components/CentralParcelDetailDrawer';
import { useDaliyTaskMonitoring, type DailyTaskMonitoringPageMode } from './hooks/useDaliyTaskMonitoring';
import { getUserInfo } from '../../../../utils/auth';
import { GENERATE_DAILY_TASK_MONITORING_SUMMARY_DOWNLOAD_REPORT, GenerateDailyTaskMonitoringSummaryDownloadReportVariables, GenerateDailyTaskMonitoringSummaryDownloadReportResponse } from './graphql/mutations/generateDailyTaskMonitoringSummaryDownloadReport';
import { GENERATE_DAILY_TASK_MONITORING_SUMMARY_DOWNLOAD_REPORT_FOR_DSP, GenerateDailyTaskMonitoringSummaryDownloadReportForDspVariables, GenerateDailyTaskMonitoringSummaryDownloadReportForDspResponse } from './graphql/mutations/generateDailyTaskMonitoringSummaryDownloadReportForDsp';
import ParcelsDownloadModal from '../../parcels/components/ParcelsDownloadModal';
import { useDownloadCooldown } from '../../../../hooks/useDownloadCooldown';

const DailyTaskMonitoringPage: React.FC<{ mode: DailyTaskMonitoringPageMode }> = ({ mode }) => {
    const getRateTextClassName = (rate?: number) => {
        if (typeof rate !== 'number') return 'text-gray-800';
        if (rate >= 95) return 'text-green-600';
        if (rate >= 85) return 'text-blue-600';
        if (rate >= 70) return 'text-yellow-600';
        return 'text-red-600';
    };
    const { t } = useTranslation();
    const {
        filters,
        setFilters,
        driverData,
        areaData,
        summaryData,
        cities,
        dsps,
        areas,
        drivers,
        drawerDrivers,
        onDspSearch,
        onAreaSearch,
        onDriverSearch,
        dspOptionsLoading,
        areaOptionsLoading,
        driverOptionsLoading,
        onDrawerDriverSearch,
        drawerDriverOptionsLoading,
        drawerVisible,
        drawerTitle,
        drawerData,
        drawerColumns,
        setDrawerVisible,
        handleDetailClick,
        handleUndeliveredDistributionClick,
        handleDeliveryProgressClick,
        handlePageChange,
        handleDetailPageChange,
        handleSortChange,
        handleDetailSortChange,
        sortConfig,
        detailSortConfig,
        detailPagination,
        detailLoading,
        detailFilters,
        setDetailFilters,
        detailStatusTypes,
        statusTypeOptions,
        handleDetailStatusTypeChange,
        dataUpdatedAtText,
        loading,
        pagination,
        tablePagination,
        orderDetailVisible,
        orderId,
        setOrderDetailVisible,
                warehouseId,
                lastDetailContext,
                dspMode,
                currentDspId,
                getCurrentSummaryDownloadArgs
            } = useDaliyTaskMonitoring(mode);

    const tableView: 'dspPerspective' | 'whsPerspective' | 'driverDimension' | 'areaDimension' = filters.viewMode === 'driver' ? 'driverDimension' : 'areaDimension';

    const handleViewModeChange = (mode: 'driver' | 'area') => {
        // 切换维度时，清空筛选条件，重新从各自数据中获取
        // 使用 setFilters 会自动重置页码（在 handleFiltersUpdate 中处理）
        setFilters({
            ...filters,
            viewMode: mode,
            selectedDsps: [],
            selectedAreas: [],
            selectedDrivers: [],
            selectedCities: []
        });
    };

    const [downloadModalVisible, setDownloadModalVisible] = useState(false);
    const [generatedFileName, setGeneratedFileName] = useState('');
    const { downloadLoading, setDownloadLoading, cooldownRemaining, getLastDownloadTime, setLastDownloadTime } = useDownloadCooldown(`dailyTaskMonitoringDownloadLastTime_${getUserInfo()?.account?.id}`);

    const [generateReportMutation] = useMutation<
        GenerateDailyTaskMonitoringSummaryDownloadReportResponse | GenerateDailyTaskMonitoringSummaryDownloadReportForDspResponse,
        GenerateDailyTaskMonitoringSummaryDownloadReportVariables | GenerateDailyTaskMonitoringSummaryDownloadReportForDspVariables
    >(dspMode ? GENERATE_DAILY_TASK_MONITORING_SUMMARY_DOWNLOAD_REPORT_FOR_DSP : GENERATE_DAILY_TASK_MONITORING_SUMMARY_DOWNLOAD_REPORT);

    const handleDownload = async () => {
        const currentTime = Date.now();
        const lastTime = getLastDownloadTime();

        if (currentTime - lastTime < 5000) {
            const remainingTime = Math.ceil((5000 - (currentTime - lastTime)) / 1000);
            message.warning(t('wms.delivery.dailyTaskMonitoring.messages.waitBeforeRetry', { remainingTime }));
            return;
        }
        if (downloadLoading) {
            message.warning(t('wms.delivery.dailyTaskMonitoring.messages.generatingInProgress'));
            return;
        }
        const args = getCurrentSummaryDownloadArgs();
        if (!args) {
            message.error(t('wms.delivery.dailyTaskMonitoring.messages.failedToStartGeneration'));
            return;
        }

        try {
            setDownloadLoading(true);
            setLastDownloadTime(currentTime);

            const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const columnConfig = args.viewMode === 'driver'
                ? [
                    { key: 'dspName', enabled: true, label: t('wms.delivery.dailyTaskMonitoring.areaColumns.dsp') },
                    { key: 'driverName', enabled: true, label: t('wms.delivery.dailyTaskMonitoring.areaColumns.driver') },
                    { key: 'allocatedParcels', enabled: true, label: t('wms.delivery.dailyTaskMonitoring.areaColumns.allocatedParcels') },
                    { key: 'transferredIn', enabled: true, label: t('wms.delivery.dailyTaskMonitoring.areaColumns.transferInToday') },
                    { key: 'pickupRate', enabled: true, label: t('wms.delivery.dailyTaskMonitoring.tableColumns.pickupRate') },
                    { key: 'deliveryRate', enabled: true, label: t('wms.delivery.dailyTaskMonitoring.tableColumns.deliveryRate') },
                    { key: 'deliveredCount', enabled: true, label: t('wms.delivery.dailyTaskMonitoring.tableColumns.delivered') },
                    { key: 'actualPickedUp', enabled: true, label: t('wms.delivery.dailyTaskMonitoring.tableColumns.actualPickedUp') },
                    { key: 'dwellTime', enabled: true, label: t('wms.delivery.dailyTaskMonitoring.areaColumns.dwellTime') },
                    { key: 'unclaimedCount', enabled: true, label: t('wms.delivery.dailyTaskMonitoring.tableColumns.unpicked') },
                    { key: 'inDelivery', enabled: true, label: t('wms.delivery.dailyTaskMonitoring.tableColumns.inDelivery') },
                    { key: 'returned', enabled: true, label: t('wms.delivery.dailyTaskMonitoring.tableColumns.failedReturned') },
                    { key: 'damaged', enabled: true, label: t('wms.delivery.dailyTaskMonitoring.tableColumns.failedNotReturned') },
                    { key: 'warehouseLost', enabled: true, label: t('wms.delivery.dailyTaskMonitoring.tableColumns.warehouseLost') }
                ]
                : [
                    { key: 'dspName', enabled: true, label: t('wms.delivery.dailyTaskMonitoring.areaColumns.dsp') },
                    { key: 'areaName', enabled: true, label: t('wms.delivery.dailyTaskMonitoring.areaColumns.area') },
                    { key: 'allocatedParcels', enabled: true, label: t('wms.delivery.dailyTaskMonitoring.tableColumns.scheduledParcels') },
                    { key: 'pickupRate', enabled: true, label: t('wms.delivery.dailyTaskMonitoring.tableColumns.pickupRate') },
                    { key: 'deliveryRate', enabled: true, label: t('wms.delivery.dailyTaskMonitoring.tableColumns.deliveryRate') },
                    { key: 'deliveredCount', enabled: true, label: t('wms.delivery.dailyTaskMonitoring.tableColumns.delivered') },
                    { key: 'actualPickedUp', enabled: true, label: t('wms.delivery.dailyTaskMonitoring.tableColumns.actualPickedUp') },
                    { key: 'unclaimedCount', enabled: true, label: t('wms.delivery.dailyTaskMonitoring.tableColumns.unpicked') },
                    { key: 'inDelivery', enabled: true, label: t('wms.delivery.dailyTaskMonitoring.tableColumns.inDelivery') },
                    { key: 'returned', enabled: true, label: t('wms.delivery.dailyTaskMonitoring.tableColumns.failedReturned') },
                    { key: 'damaged', enabled: true, label: t('wms.delivery.dailyTaskMonitoring.tableColumns.failedNotReturned') },
                    { key: 'warehouseLost', enabled: true, label: t('wms.delivery.dailyTaskMonitoring.tableColumns.warehouseLost') }
                ];
            const response = await generateReportMutation({
                variables: {
                    taskDate: args.taskDate,
                    warehouseId: args.warehouseId,
                    warehouseIds: args.warehouseIds,
                    dspIds: args.dspIds,
                    areaIds: args.areaIds,
                    driverIds: args.driverIds,
                    cityNames: args.cityNames,
                    viewMode: args.viewMode,
                    columnConfig,
                    timezone: userTimezone,
                    sortBy: args.sortBy,
                    sortDirection: args.sortDirection,
                }
            });
            const fileName = dspMode
                ? (response.data as GenerateDailyTaskMonitoringSummaryDownloadReportForDspResponse | undefined)?.generateDailyTaskMonitoringSummaryDownloadReportForDsp
                : (response.data as GenerateDailyTaskMonitoringSummaryDownloadReportResponse | undefined)?.generateDailyTaskMonitoringSummaryDownloadReport;
            if (fileName) {
                setGeneratedFileName(fileName);
                setDownloadModalVisible(true);
            } else {
                message.error(t('wms.delivery.dailyTaskMonitoring.messages.failedToStartGeneration'));
            }
        } catch (error: any) {
            let errorMessage = t('wms.delivery.dailyTaskMonitoring.messages.generationFailedTryLater');
            if (error?.graphQLErrors?.[0]) {
                errorMessage = error.graphQLErrors[0].extensions?.message || error.graphQLErrors[0].message || errorMessage;
            } else if (error?.message) {
                errorMessage = error.message;
            }
            message.error(errorMessage);
        } finally {
            setDownloadLoading(false);
        }
    };

    const currentData = filters.viewMode === 'driver' ? driverData : areaData;
    const deliveryProgress = summaryData?.deliveryProgress;
    const undeliveredDistribution = summaryData?.undeliveredDistribution;

    const renderClickableNumber = (
        value: number,
        onClick?: () => void
    ) => {
        if (value <= 0 || !onClick) {
            return <span className="text-2xl text-gray-800">{(value ?? 0).toString()}</span>;
        }
        return (
            <a
                href="#"
                onClick={(e) => {
                    e.preventDefault();
                    onClick();
                }}
                style={{ color: '#1890ff' }}
                className="text-2xl"
            >
                {value.toString()}
            </a>
        );
    };

    return (
            <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
                {/* 顶部筛选器 */}
                <TaskMonitoringFilter
                    filters={filters}
                    onFiltersChange={setFilters}
                    cities={cities}
                    dsps={dsps}
                    areas={areas}
                    drivers={drivers}
                    showResetButton={false}
                    onDspSearch={onDspSearch}
                    onAreaSearch={onAreaSearch}
                    onDriverSearch={onDriverSearch}
                    dspOptionsLoading={dspOptionsLoading}
                    areaOptionsLoading={areaOptionsLoading}
                    driverOptionsLoading={driverOptionsLoading}
                    dspMode={dspMode}
                />

                {/* 整体统计信息 */}
                <div className="flex gap-2 mb-6">
                    <IndicatorCardWithCustomTitle
                        title={t('wms.delivery.dailyTaskMonitoring.summary.viewTodayDeliveryProgress')}
                        titleBackgroundColor="bg-red-500 text-white"
                        // 前3个（应派/实领/已妥投）靠近；后2个（领取率/妥投率）靠近；两组之间留出间距
                        itemsContainerClassName="flex items-start gap-0.5"
                        items={[
                            {
                                label: (
                                    <span className="text-xs text-gray-400 pr-4">
                                        {t('wms.delivery.dailyTaskMonitoring.summary.scheduledParcels')}
                                    </span>
                                ),
                                value: renderClickableNumber(
                                    Number(deliveryProgress?.scheduledParcels ?? 0),
                                    () => handleDeliveryProgressClick('allocatedParcels')
                                ),
                                color: 'blue',
                                showArrow: true,
                                arrowClassName: 'text-gray-400 text-2xl leading-none',
                                containerClassName: 'min-w-[88px]'
                            },
                            {
                                label: (
                                    <span className="text-xs text-gray-400 pr-4">
                                        {t('wms.delivery.dailyTaskMonitoring.summary.actualPickedUp')}
                                    </span>
                                ),
                                value: renderClickableNumber(
                                    Number(deliveryProgress?.actualPickedUp ?? 0),
                                    () => handleDeliveryProgressClick('pickedUpFromWarehouse')
                                ),
                                color: 'blue',
                                showArrow: true,
                                arrowClassName: 'text-gray-400 text-sm',
                                containerClassName: 'min-w-[88px]'
                            },
                            {
                                label: t('wms.delivery.dailyTaskMonitoring.summary.deliveredParcels'),
                                value: renderClickableNumber(
                                    Number(deliveryProgress?.deliveredParcels ?? 0),
                                    () => handleDeliveryProgressClick('delivered')
                                ),
                                color: 'blue',
                                containerClassName: 'min-w-[88px]'
                            },
                            {
                                label: (
                                    <div className="flex flex-col items-center">
                                        <span className="text-xs text-gray-400">{t('wms.delivery.dailyTaskMonitoring.summary.pickupRate')}</span>
                                        <span className="text-xs text-gray-300">{t('wms.delivery.dailyTaskMonitoring.summary.pickupRateLabel')}</span>
                                    </div>
                                ),
                                value: (
                                    <span className={`text-2xl ${getRateTextClassName(deliveryProgress?.pickupRate)}`}>
                                        {typeof deliveryProgress?.pickupRate === 'number' ? `${deliveryProgress.pickupRate.toFixed(2)}%` : '0.00%'}
                                    </span>
                                ),
                                containerClassName: 'ml-6 min-w-[135px]'
                            },
                            {
                                label: (
                                    <div className="flex flex-col items-center">
                                        <span className="text-xs text-gray-400">{t('wms.delivery.dailyTaskMonitoring.summary.deliveryRate')}</span>
                                        <span className="text-xs text-gray-300">{t('wms.delivery.dailyTaskMonitoring.summary.deliveryRateLabel')}</span>
                                    </div>
                                ),
                                value: (
                                    <span className={`text-2xl ${getRateTextClassName(deliveryProgress?.deliveryRate)}`}>
                                        {typeof deliveryProgress?.deliveryRate === 'number' ? `${deliveryProgress.deliveryRate.toFixed(2)}%` : '0.00%'}
                                    </span>
                                ),
                                containerClassName: 'min-w-[130px]'
                            }
                        ]}
                    />
                    <div className="flex flex-col gap-2 border-l-4 border-purple-600 pl-2.5">
                        <p className="text-lg font-medium px-2 py-1 rounded text-center">
                            {t('wms.delivery.dailyTaskMonitoring.undeliveredDistribution.title')}
                        </p>
                        <div className="overflow-x-auto min-w-0">
                            {/* 2行grid：列按内容自适应(min-content)，0时窄、大数值时不重叠，超出时横向滚动 */}
                            <div className="grid grid-rows-2 w-full grid-cols-[minmax(min-content,1fr)_16px_minmax(min-content,1fr)_16px_minmax(min-content,1fr)_16px_minmax(min-content,1.33fr)_16px_minmax(min-content,1.33fr)_16px_minmax(min-content,1.33fr)] gap-x-1 gap-y-1">
                                {/* row 1: numbers + symbols */}
                                <div className="flex justify-center items-center pt-2 min-w-0">
                                    {renderClickableNumber(
                                        undeliveredDistribution?.totalUndelivered ?? 0,
                                        () => handleUndeliveredDistributionClick('undelivered')
                                    )}
                                </div>
                                <div className="flex justify-center items-center pt-2 shrink-0"><span className="text-2xl font-semibold text-gray-500 leading-none">=</span></div>
                                <div className="flex justify-center items-center pt-2 min-w-0">{renderClickableNumber(undeliveredDistribution?.unpicked ?? 0, () => handleUndeliveredDistributionClick('unclaimed'))}</div>
                                <div className="flex justify-center items-center pt-2 shrink-0"><span className="text-2xl font-semibold text-gray-500 leading-none">+</span></div>
                                <div className="flex justify-center items-center pt-2 min-w-0">{renderClickableNumber(undeliveredDistribution?.inDelivery ?? 0, () => handleUndeliveredDistributionClick('inDelivery'))}</div>
                                <div className="flex justify-center items-center pt-2 shrink-0"><span className="text-2xl font-semibold text-gray-500 leading-none">+</span></div>
                                <div className="flex justify-center items-center pt-2 min-w-0">{renderClickableNumber(undeliveredDistribution?.failedReturned ?? 0, () => handleUndeliveredDistributionClick('returned'))}</div>
                                <div className="flex justify-center items-center pt-2 shrink-0"><span className="text-2xl font-semibold text-gray-500 leading-none">+</span></div>
                                <div className="flex justify-center items-center pt-2 min-w-0">{renderClickableNumber(undeliveredDistribution?.failedNotReturned ?? 0, () => handleUndeliveredDistributionClick('damaged'))}</div>
                                <div className="flex justify-center items-center pt-2 shrink-0"><span className="text-2xl font-semibold text-gray-500 leading-none">+</span></div>
                                <div className="flex justify-center items-center pt-2 min-w-0">{renderClickableNumber(undeliveredDistribution?.warehouseLost ?? 0, () => handleUndeliveredDistributionClick('warehouseLost'))}</div>

                                {/* row 2: labels (symbols row labels empty) */}
                                <div className="flex justify-center pb-2 px-1 min-w-0">
                                    <span className="text-xs text-gray-400 text-center break-words leading-tight">
                                        {t('wms.delivery.dailyTaskMonitoring.undeliveredDistribution.undelivered')}
                                    </span>
                                </div>
                                <div className="flex justify-center pb-2 shrink-0"><span className="text-xs text-transparent">_</span></div>
                                <div className="flex justify-center pb-2 px-1 min-w-0">
                                    <span className="text-xs text-gray-400 text-center break-words leading-tight">
                                        {t('wms.delivery.dailyTaskMonitoring.undeliveredDistribution.unpicked')}
                                    </span>
                                </div>
                                <div className="flex justify-center pb-2 shrink-0"><span className="text-xs text-transparent">_</span></div>
                                <div className="flex justify-center pb-2 px-1 min-w-0">
                                    <span className="text-xs text-gray-400 text-center break-words leading-tight">
                                        {t('wms.delivery.dailyTaskMonitoring.undeliveredDistribution.inDelivery')}
                                    </span>
                                </div>
                                <div className="flex justify-center pb-2 shrink-0"><span className="text-xs text-transparent">_</span></div>
                                <div className="flex justify-center pb-2 px-1 min-w-0">
                                    <span className="text-xs text-gray-400 text-center break-words leading-tight">
                                        {t('wms.delivery.dailyTaskMonitoring.undeliveredDistribution.failedReturned')}
                                    </span>
                                </div>
                                <div className="flex justify-center pb-2 shrink-0"><span className="text-xs text-transparent">_</span></div>
                                <div className="flex justify-center pb-2 px-1 min-w-0">
                                    <span className="text-xs text-gray-400 text-center break-words leading-tight">
                                        {t('wms.delivery.dailyTaskMonitoring.undeliveredDistribution.failedNotReturned')}
                                    </span>
                                </div>
                                <div className="flex justify-center pb-2 shrink-0"><span className="text-xs text-transparent">_</span></div>
                                <div className="flex justify-center pb-2 px-1 min-w-0">
                                    <span className="text-xs text-gray-400 text-center break-words leading-tight">
                                        {t('wms.delivery.dailyTaskMonitoring.undeliveredDistribution.warehouseLost')}
                                    </span>
                                </div>
                            </div>
                            <div className="mt-2 pt-3 pl-[0.65rem]">
                                <p className="text-sm text-gray-400 text-left">
                                    {t('wms.delivery.dailyTaskMonitoring.undeliveredDistribution.failedReturnedNote')}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 维度切换按钮 + 更新时间 */}
                <div className="mb-4">
                    <div className="flex items-center gap-3">
                    <Space>
                                <Button
                                    type={filters.viewMode === 'driver' ? 'primary' : 'default'}
                                    icon={<UserOutlined />}
                                    onClick={() => handleViewModeChange('driver')}
                                    className="w-auto min-w-24"
                                >
                                    <span className="font-bold">{t('wms.delivery.dailyTaskMonitoring.dimensions.driverDimension')}</span>
                                </Button>
                                <Button
                                    type={filters.viewMode === 'area' ? 'primary' : 'default'}
                                    icon={<EnvironmentOutlined />}
                                    onClick={() => handleViewModeChange('area')}
                                    className="w-auto min-w-24"
                                >
                                    <span className="font-bold">{t('wms.delivery.dailyTaskMonitoring.dimensions.areaDimension')}</span>
                                </Button>
                    </Space>
                    {dataUpdatedAtText && (
                        <span className="text-sm text-red-500 font-bold">
                            {t('wms.delivery.dailyTaskMonitoring.dataUpdatedAt')} {dataUpdatedAtText}
                        </span>
                    )}
                    <div className="ml-auto">
                        <Button
                            type="primary"
                            onClick={handleDownload}
                            loading={cooldownRemaining > 0}
                            disabled={downloadLoading || cooldownRemaining > 0}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">
                                {cooldownRemaining > 0
                                    ? `${t('components.button.download')} ${cooldownRemaining}s`
                                    : t('components.button.download')
                                }
                            </span>
                        </Button>
                    </div>
                    </div>
                </div>


                {/* 数据表格 */}
                <TaskMonitoringTable
                    data={currentData}
                    onDetailClick={handleDetailClick}
                    viewMode={filters.viewMode}
                    tableView={tableView}
                    loading={loading}
                    pagination={tablePagination}
                    sortConfig={sortConfig}
                    onSortChange={handleSortChange}
                    filters={filters}
                />

                {/* 分页 */}
                <TablePaginationBar
                    total={pagination?.total || 0}
                    current={pagination?.current || 1}
                    pageSize={pagination?.pageSize || 20}
                    onChange={handlePageChange}
                />

                {/* 详情抽屉 */}
                <DailyTaskDetailDrawer
                    visible={drawerVisible}
                    title={drawerTitle}
                    data={drawerData}
                    columns={drawerColumns}
                    loading={detailLoading}
                    pagination={detailPagination}
                    onClose={() => setDrawerVisible(false)}
                    onSortChange={handleDetailSortChange}
                    currentSortBy={detailSortConfig.sortBy}
                    currentSortDirection={detailSortConfig.sortDirection}
                    onPageChange={handleDetailPageChange}
                    detailFilters={detailFilters}
                    onDetailFiltersChange={(next) => setDetailFilters((prev) => ({ ...prev, ...next }))}
                    dsps={dsps}
                    areas={areas}
                    drivers={drawerDrivers}
                    onDriverSearch={onDrawerDriverSearch}
                    driverOptionsLoading={drawerDriverOptionsLoading}
                    viewMode={filters.viewMode}
                    taskDate={filters.taskDate.format('YYYY-MM-DD')}
                    warehouseId={warehouseId}
                    statusTypeOptions={['allocatedParcels', 'transferred_in', 'pickedUpFromWarehouse', 'delivered', 'unclaimed', 'inDelivery', 'returned', 'damaged', 'warehouseLost', 'undelivered'].includes(String(lastDetailContext?.type))
                        ? statusTypeOptions
                        : undefined}
                    selectedStatusTypes={detailStatusTypes}
                    onStatusTypesChange={['allocatedParcels', 'transferred_in', 'pickedUpFromWarehouse', 'delivered', 'unclaimed', 'inDelivery', 'returned', 'damaged', 'warehouseLost', 'undelivered'].includes(String(lastDetailContext?.type))
                        ? handleDetailStatusTypeChange
                        : undefined}
                    statusField={lastDetailContext?.statusField ?? (lastDetailContext?.type ? (() => {
                        // 如果 lastDetailContext 中没有 statusField，则从 type 映射（向后兼容）
                        const type = lastDetailContext.type;
                        const statusFieldMap: Record<string, string> = {
                            // 配送效率类型
                            'on_time_delivery': 'isOnTimeDelivery',
                            'overdue_delivery': 'isOverdueDelivery',
                            'transferred_in': 'isTransferredIn',
                            'transferred_out': 'isTransferredOut',
                            'inDelivery': 'isInDelivery',
                            'allocatedParcels': 'isAllocatedParcels',
                            'pickedUpFromWarehouse': 'isPickedUpFromWarehouse',
                            'warehouseLost': 'isWarehouseLost',
                            'returned': 'isReturned',
                            'damaged': 'isDamaged',
                            'unclaimed': 'isUnclaimed'
                        };
                        return statusFieldMap[type] || undefined;
                    })() : undefined)}
                    lastDetailContext={lastDetailContext}
                    currentDspId={currentDspId ? (typeof currentDspId === 'string' ? Number(currentDspId) : currentDspId) : undefined}
                    dspMode={dspMode}
                />

                {/* 订单详情抽屉 */}
                <CentralParcelDetailDrawer
                    showInterceptionExemptionAlert={true}
                    visible={orderDetailVisible}
                    orderId={orderId}
                    userRole={getUserInfo()?.account?.role?.name || ''}
                    onClose={() => setOrderDetailVisible(false)}
                />

                <ParcelsDownloadModal
                    visible={downloadModalVisible}
                    onClose={() => setDownloadModalVisible(false)}
                    fileName={generatedFileName}
                />
            </div>
    );
};

export default DailyTaskMonitoringPage;